import { useState, useEffect, useCallback } from 'react';
import { isEqual } from 'lodash-es';
import BuildingAndPestPayable from './BuildingAndPestPayable';
import './BuildingAndPest.scss';
import { ErrorState } from 'types/Payable';
import {
  Deposit,
  DepositCondition,
  DepositDayType,
  DepositPayable,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import { AgxRadioGroup, AgxSpacer } from '@urbanx/agx.ui-components2';

interface BuildingAndPestProps {
  id: string;
  label: string;
  onValueChanged: (change: { id: string; value: Deposit }) => void;
  defaultValue: Deposit;
  validate?: boolean;
  error?: ErrorState;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const yesBuildingAndPest = 'yesBuildingAndPest';

const radioOptions = [
  {
    label: 'Not subject to building & pest',
    value: DepositCondition.NotApplicable,
  },
  {
    label: 'Subject to building & pest',
    value: yesBuildingAndPest,
  },
];

const getNormalizedBuildingAndPestCondition = (defaultValue: Deposit): Deposit => ({
  ...defaultValue,
  condition: defaultValue?.condition ?? DepositCondition.NotApplicable,
});

const BuildingAndPest = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  validate,
  error,
  specialConditions,
  campaignId,
}: BuildingAndPestProps) => {
  const normalizedDefaultValue =
    getNormalizedBuildingAndPestCondition(defaultValue);
  const [buildingAndPestCondition, setBuildingAndPestCondition] =
    useState<Deposit>(normalizedDefaultValue);

  useEffect(() => {
    if (isEqual(normalizedDefaultValue, buildingAndPestCondition)) return;

    onValueChanged({ id, value: buildingAndPestCondition });
  }, [buildingAndPestCondition, id, normalizedDefaultValue, onValueChanged]);

  const onBuildingAndPestConditionChange = useCallback(
    ({ value }: { value: Deposit }) => {
      setBuildingAndPestCondition(prev => {
        const nextValue = {
          ...prev,
          ...value,
        };

        return isEqual(prev, nextValue) ? prev : nextValue;
      });
    },
    []
  );

  const onCheckChanged = useCallback((value: string) => {
    if (value === yesBuildingAndPest) {
      setBuildingAndPestCondition(prev => {
        const nextValue = {
          ...prev,
          condition: DepositCondition.NdaysFromDate,
        };

        return isEqual(prev, nextValue) ? prev : nextValue;
      });
    } else {
      setBuildingAndPestCondition(prev => {
        const nextValue = {
          condition: DepositCondition.NotApplicable,
          depositPayable: DepositPayable.NDaysFrom,
          dayType: DepositDayType.Days,
          other: undefined,
          daysFromDate: undefined,
          referenceDate: undefined,
          specialConditionId: undefined,
        };

        return isEqual(prev, nextValue) ? prev : nextValue;
      });
    }
  }, []);

  return (
    <AgxRadioGroup
      id={`deposit-radio-group-${id}`}
      label={label}
      layout={'vertical'}
      options={radioOptions}
      value={
        buildingAndPestCondition.condition === DepositCondition.NotApplicable
          ? DepositCondition.NotApplicable
          : yesBuildingAndPest
      }
      onChange={onCheckChanged}
      subContent={
        buildingAndPestCondition.condition !==
        DepositCondition.NotApplicable ? (
          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            padding="md"
            key="customAmountInput"
            fill
          >
            <BuildingAndPestPayable
              id={id}
              onValueChanged={onBuildingAndPestConditionChange}
              defaultValue={buildingAndPestCondition}
              validate={validate}
              error={error}
              required={true}
              specialConditions={specialConditions}
              campaignId={campaignId}
            />
          </AgxSpacer>
        ) : undefined
      }
    />
  );
};

export default BuildingAndPest;
